import {useState} from 'react'
import Swal from 'sweetalert2'
import {KTIcon} from '../../../../../../_metronic/helpers'
import {cleanMask} from '../../../../../../utils/cleanMask'
import {cpfMask} from '../../../../../../utils/cpfMask'
import {useMetric} from '../../core/MetricProvider'
import {useMetricOptions} from '../../core/useMetricOptions'
import {MetricFilters} from '../../core/_models'
import {exportMetricReport} from '../../core/_requests'
import {DateRangeFilter, isRangeInvalid} from './filters/DateRangeFilter'
import {MultiSelectFilter} from './filters/MultiSelectFilter'

const SALE_TYPES = [
  {value: '1', label: 'Avulso'},
  {value: '2', label: 'Pacote'},
]

type ListKey =
  | 'gender'
  | 'status'
  | 'how_met_id'
  | 'purchase_range'
  | 'last_purchase_range'
  | 'product_code'
  | 'category_id'
  | 'sale_type'

const ListToolbar = () => {
  const {isLoading, applyFilters, filtersQuery} = useMetric()
  const options = useMetricOptions()
  const [draft, setDraft] = useState<MetricFilters>({})

  const set = (key: keyof MetricFilters) => (value: string | string[]) =>
    setDraft((current) => ({...current, [key]: value}))

  const text = (key: Exclude<keyof MetricFilters, ListKey>) => (draft[key] as string) ?? ''
  const list = (key: ListKey) => draft[key] ?? []

  const hasErrors =
    isRangeInvalid(draft.from, draft.to) ||
    isRangeInvalid(draft.created_at_start, draft.created_at_end) ||
    isRangeInvalid(draft.birth_date_start, draft.birth_date_end)

  const closeDrawer = () => document.querySelector<HTMLButtonElement>('#kt_drawer_filter_close')?.click()

  const filterData = () => {
    applyFilters({...draft, document: draft.document ? cleanMask(draft.document) : ''})
    closeDrawer()
  }

  const resetData = () => {
    setDraft({})
    applyFilters({})
  }

  const downloadFile = async () => {
    try {
      const result = await exportMetricReport(filtersQuery)
      Swal.fire({
        icon: 'info',
        title: 'Relatório em processamento',
        text: result.message,
        confirmButtonColor: '#d33',
      })
    } catch (error: any) {
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: error?.response?.data?.message,
        timer: 2000,
      })
    }
  }

  return (
    <>
      <button
        disabled={isLoading}
        type='button'
        className='btn btn-light-danger me-3'
        id='kt_drawer_filter_button'
      >
        <KTIcon iconName='filter' className='fs-2' />
        Filtrar
      </button>
      <div
        id='kt_drawer_filter'
        className='bg-body'
        data-kt-drawer='true'
        data-kt-drawer-name='activities'
        data-kt-drawer-activate='true'
        data-kt-drawer-overlay='true'
        data-kt-drawer-width="{default:'300px', 'lg': '500px'}"
        data-kt-drawer-direction='end'
        data-kt-drawer-toggle='#kt_drawer_filter_button'
        data-kt-drawer-close='#kt_drawer_filter_close'
      >
        <div className='card shadow-none rounded-0 w-100'>
          <div className='card-header' id='kt_activities_header'>
            <h3 className='card-title fw-bolder text-dark'>Opções de Filtro</h3>

            <div className='card-toolbar'>
              <button
                type='button'
                className='btn btn-sm btn-icon btn-active-light-danger me-n5'
                id='kt_drawer_filter_close'
              >
                <KTIcon iconName='cross' className='fs-1' />
              </button>
            </div>
          </div>
          <div className='card-body position-relative overflow-auto' id='kt_activities_body'>
            <div className='px-7 py-5'>
              <DateRangeFilter
                label='Período de vendas'
                start={text('from')}
                end={text('to')}
                onChangeStart={set('from')}
                onChangeEnd={set('to')}
              />
              <div className='form-text mt-n3 mb-5'>
                Sem período informado, considera os últimos 12 meses.
              </div>

              <div className='mb-5'>
                <label className='form-label fs-6 fw-bold'>Nome:</label>
                <input
                  type='text'
                  className='form-control form-control-solid'
                  placeholder='Informe o nome'
                  value={text('name')}
                  onChange={(e) => set('name')(e.target.value)}
                />
              </div>

              <div className='mb-5'>
                <label className='form-label fs-6 fw-bold'>CPF:</label>
                <input
                  type='text'
                  className='form-control form-control-solid'
                  placeholder='Informe o CPF'
                  value={cpfMask(text('document'))}
                  onChange={(e) => e.target.value.length <= 14 && set('document')(e.target.value)}
                />
              </div>

              <DateRangeFilter
                label='Data de cadastro'
                start={text('created_at_start')}
                end={text('created_at_end')}
                onChangeStart={set('created_at_start')}
                onChangeEnd={set('created_at_end')}
              />

              <DateRangeFilter
                label='Data de nascimento'
                start={text('birth_date_start')}
                end={text('birth_date_end')}
                onChangeStart={set('birth_date_start')}
                onChangeEnd={set('birth_date_end')}
              />

              <MultiSelectFilter
                label='Sexo'
                placeholder='Selecione o sexo'
                options={options.genders}
                value={list('gender')}
                onChange={set('gender')}
              />

              <MultiSelectFilter
                label='Status'
                placeholder='Selecione o status'
                options={options.statuses}
                value={list('status')}
                onChange={set('status')}
              />

              <div className='mb-5'>
                <label className='form-label fs-6 fw-bold'>Possui distrato:</label>
                <select
                  className='form-select form-select-solid'
                  value={text('has_distract')}
                  onChange={(e) => set('has_distract')(e.target.value)}
                >
                  <option value=''>Todos</option>
                  <option value='1'>Sim</option>
                  <option value='0'>Não</option>
                </select>
              </div>

              <MultiSelectFilter
                label='Origem (como conheceu)'
                placeholder='Selecione a origem'
                options={options.howMetOptions}
                value={list('how_met_id')}
                onChange={set('how_met_id')}
              />

              <MultiSelectFilter
                label='Faixa de compra (R$)'
                placeholder='Selecione a faixa'
                options={options.purchaseRanges}
                value={list('purchase_range')}
                onChange={set('purchase_range')}
              />

              <MultiSelectFilter
                label='Tempo desde a última compra'
                placeholder='Selecione o tempo'
                options={options.lastPurchaseRanges}
                value={list('last_purchase_range')}
                onChange={set('last_purchase_range')}
              />

              <MultiSelectFilter
                label='Produto'
                placeholder='Selecione o produto'
                options={options.products}
                value={list('product_code')}
                onChange={set('product_code')}
              />

              <MultiSelectFilter
                label='Categoria'
                placeholder='Selecione a categoria'
                options={options.categories}
                value={list('category_id')}
                onChange={set('category_id')}
              />

              <MultiSelectFilter
                label='Tipo de venda'
                placeholder='Selecione o tipo'
                options={SALE_TYPES}
                value={list('sale_type')}
                onChange={set('sale_type')}
              />

              <div className='mb-5'>
                <label className='form-label fs-6 fw-bold'>Ticket médio acima de (R$):</label>
                <input
                  type='number'
                  min={0}
                  step='0.01'
                  className='form-control form-control-solid'
                  placeholder='0,00'
                  value={text('min_ticket')}
                  onChange={(e) => set('min_ticket')(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className='card-footer py-5 text-center' id='kt_activities_footer'>
            <div className='d-flex justify-content-end'>
              <button
                type='button'
                disabled={isLoading}
                onClick={resetData}
                className='btn btn-secondary fw-bold me-2 px-6'
              >
                Limpar
              </button>
              <button
                disabled={isLoading || hasErrors}
                type='button'
                onClick={filterData}
                className='btn btn-danger fw-bold px-6'
              >
                Filtrar
              </button>
            </div>
          </div>
        </div>
      </div>
      <button
        disabled={isLoading}
        type='button'
        className='btn btn-light-danger me-3'
        onClick={() => void downloadFile()}
      >
        <KTIcon iconName='exit-up' className='fs-2' />
        Export
      </button>
    </>
  )
}

export {ListToolbar}
